import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../common/api';

export interface Hospital {
  id: string;
  name: string;
  address: string;
}

export interface HospitalState {
  hospitals: Hospital[];
  loading: boolean;
  error: string | null;
}

const initialState: HospitalState = {
  hospitals: [],
  loading: false,
  error: null,
};

const messageFromError = (error: any) => {
  if (error.response) {
    // Server responded with a status other than 200 range
    return error.response.data.message;
  } else if (error.request) {
    // Request was made but no response received
    return 'No response from server';
  } else {
    // Something else happened while setting up the request
    return error.message;
  }
};

export const fetchHospitals = createAsyncThunk<Hospital[], void, { rejectValue: string }>('hospital/fetchHospitals', async (_, thunkAPI) => {
  try {
    const response = await api.get('/api/v1/hospitals');
    return response.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(`Failed to load hospitals - ${messageFromError(error)}`);
  }
});

export const createHospital = createAsyncThunk<Hospital, { name: string; address: string; studyId: string }, { rejectValue: string }>(
  'hospital/createHospital',
  async (hospitalData, thunkAPI) => {
    try {
      const response = await api.post('/api/v1/hospitals', { name: hospitalData.name, address: hospitalData.address, studyId: hospitalData.studyId });
      thunkAPI.dispatch(fetchHospitals());
      return response.data;
    } catch (error: any) {
      console.log(error);
      return thunkAPI.rejectWithValue(`Failed to create hospital - ${error.response.data.message}`);
    }
  },
);
export const updateHospital = createAsyncThunk<Hospital, { id: string; name: string; address: string; studyId: string }, { rejectValue: string }>(
  'hospital/updateHospital',
  async (hospitalData, thunkAPI) => {
    try {
      // create a new data object copying hospitalData without the study property
      const hData = { id: hospitalData.id, name: hospitalData.name, address: hospitalData.address, studyId: hospitalData.studyId };

      const response = await api.patch(`/api/v1/hospitals/${hospitalData.id}`, hData);
      thunkAPI.dispatch(fetchHospitals());
      return response.data;
    } catch (error: any) {
      console.log(error);
      return thunkAPI.rejectWithValue(`Failed to update hospital - ${messageFromError(error)}`);
    }
  },
);

export const deleteHospital = createAsyncThunk<string, string, { rejectValue: string }>('hospital/deleteHospital', async (id, thunkAPI) => {
  try {
    await api.delete(`/api/v1/hospitals/${id}`);
    thunkAPI.dispatch(fetchHospitals());
    return id;
  } catch (error: any) {
    console.log(error);
    return thunkAPI.rejectWithValue(`Failed to delete hospital - ${messageFromError(error)}`);
  }
});

const hospitalSlice = createSlice({
  name: 'hospital',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchHospitals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHospitals.fulfilled, (state, action: PayloadAction<Hospital[]>) => {
        state.hospitals = action.payload;
        state.loading = false;
      })
      .addCase(fetchHospitals.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch hospitals';
      })
      .addCase(createHospital.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createHospital.fulfilled, (state, action: PayloadAction<Hospital>) => {
        state.hospitals.push(action.payload);
        state.loading = false;
      })
      .addCase(createHospital.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create hospital';
      })
      .addCase(updateHospital.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateHospital.fulfilled, (state, action: PayloadAction<Hospital>) => {
        state.hospitals.push(action.payload);
        state.loading = false;
      })
      .addCase(updateHospital.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create hospital';
      })
      .addCase(deleteHospital.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteHospital.fulfilled, (state, action: PayloadAction<string>) => {
        state.hospitals = state.hospitals.filter((hospital) => hospital.id !== action.payload);
        state.loading = false;
      })
      .addCase(deleteHospital.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload || 'Failed to delete hospital';
      });
  },
});

export default hospitalSlice.reducer;
